import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Bot, Trash2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { AgentCard, NewAgentCard } from '@/components/agents/agent-card'
import { AgentHistoryDialog } from '@/components/agents/agent-history-dialog'
import { agentService } from '@/services/agent'
import { useOrganizationStore } from '@/stores/organization-store'
import { useDebounce } from '@/hooks/use-debounce'
import type { Agent } from '@/schemas/agent'
import type { AxiosError } from 'axios'

// Filter and sort types
type StatusFilter = 'all' | 'active' | 'inactive'
type SortOption = 'name_asc' | 'name_desc' | 'created_desc' | 'created_asc'

// Dialog state interface for reusability
interface DialogState<T> {
  isOpen: boolean
  isProcessing: boolean
  error: string | null
  context: T | null
}

// Initial dialog state helper
function createInitialDialogState<T>(): DialogState<T> {
  return {
    isOpen: false,
    isProcessing: false,
    error: null,
    context: null,
  }
}

// Status filter options (will use i18n keys)
const STATUS_OPTIONS: { value: StatusFilter; i18nKey: string }[] = [
  { value: 'all', i18nKey: 'allStatus' },
  { value: 'active', i18nKey: 'activeOnly' },
  { value: 'inactive', i18nKey: 'inactiveOnly' },
]

// Sort options (will use i18n keys)
const SORT_OPTIONS: { value: SortOption; i18nKey: string }[] = [
  { value: 'name_asc', i18nKey: 'sortNameAsc' },
  { value: 'name_desc', i18nKey: 'sortNameDesc' },
  { value: 'created_desc', i18nKey: 'sortNewest' },
  { value: 'created_asc', i18nKey: 'sortOldest' },
]

// Loading skeleton component
function AgentCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-14 h-14 rounded-xl bg-gray-700/50" />
        <div className="w-16 h-6 rounded-full bg-gray-700/50" />
      </div>
      <div className="space-y-2 mb-4">
        <div className="h-5 bg-gray-700/50 rounded w-3/4" />
        <div className="h-4 bg-gray-700/50 rounded w-1/2" />
      </div>
      <div className="h-10 bg-gray-700/50 rounded w-full" />
    </Card>
  )
}

// Empty state component
function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  const { t } = useTranslation('agents')
  const navigate = useNavigate()

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <Bot className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {hasFilters ? t('list.noAgentsFiltered') : t('list.noAgents')}
      </h3>
      <p className="text-gray-500 text-center max-w-md mb-6">
        {hasFilters
          ? t('list.noAgentsFilteredDesc')
          : t('list.noAgentsDesc')}
      </p>
      {!hasFilters && (
        <Button
          variant="action-indigo"
          className="gap-2"
          onClick={() => navigate('/agents/create')}
        >
          <Plus className="w-5 h-5" />
          {t('list.createFirst')}
        </Button>
      )}
    </div>
  )
}

// No project selected state
function NoProjectState() {
  const { t } = useTranslation('agents')

  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-6">
        <Bot className="w-10 h-10 text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('list.noProjectSelected')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {t('list.noProjectDesc')}
      </p>
    </div>
  )
}

export function AgentsPage() {
  const { t } = useTranslation(['agents', 'common'])
  const navigate = useNavigate()
  const { currentProject } = useOrganizationStore()

  // State
  const [agents, setAgents] = useState<Agent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sortOption, setSortOption] = useState<SortOption>('name_asc')

  // Duplicate dialog state (grouped)
  const [duplicateDialog, setDuplicateDialog] = useState<
    DialogState<{ id: string; name: string }>
  >(createInitialDialogState())
  const [duplicateName, setDuplicateName] = useState('')

  // Delete dialog state (grouped)
  const [deleteDialog, setDeleteDialog] = useState<
    DialogState<{ id: string; name: string }>
  >(createInitialDialogState())

  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [historyAgentId, setHistoryAgentId] = useState<string | null>(null)
  const [historyAgentName, setHistoryAgentName] = useState('')

  // Debounced search
  const debouncedSearch = useDebounce(searchTerm, 300)

  // Fetch agents when project or filters change
  useEffect(() => {
    const controller = new AbortController()

    async function fetchAgents() {
      if (!currentProject?.id) {
        setAgents([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const isActive =
          statusFilter === 'all' ? undefined : statusFilter === 'active'

        const response = await agentService.listAgents(
          currentProject.id,
          {
            search: debouncedSearch || undefined,
            is_active: isActive,
          },
          { signal: controller.signal }
        )
        setAgents(response.agents)
      } catch (error) {
        if ((error as Error).name !== 'CanceledError') {
          console.error('Failed to fetch agents:', error)
          setAgents([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchAgents()

    return () => {
      controller.abort()
    }
  }, [currentProject?.id, debouncedSearch, statusFilter])

  // Sorted agents (frontend sorting since API doesn't support it)
  const sortedAgents = useMemo(() => {
    return [...agents].sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.name.localeCompare(b.name)
        case 'name_desc':
          return b.name.localeCompare(a.name)
        case 'created_desc':
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
        case 'created_asc':
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
        default:
          return 0
      }
    })
  }, [agents, sortOption])

  // Check if any filters are active
  const hasFilters = searchTerm !== '' || statusFilter !== 'all'

  // Handlers
  const handleCreateAgent = () => {
    navigate('/agents/create')
  }

  const handleAgentClick = (agentId: string) => {
    navigate(`/agents/${agentId}`)
  }

  const handleEditAgent = (agentId: string) => {
    navigate(`/agents/${agentId}/edit`)
  }

  // Duplicate dialog handlers
  const handleDuplicateAgent = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId)
    if (agent) {
      setDuplicateName(`${agent.name} (${t('common:actions.copy').toLowerCase()})`)
      setDuplicateDialog({
        isOpen: true,
        isProcessing: false,
        error: null,
        context: { id: agentId, name: agent.name },
      })
    }
  }

  const handleConfirmDuplicate = async () => {
    if (!currentProject?.id || !duplicateDialog.context || !duplicateName.trim()) {
      return
    }

    setDuplicateDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      const response = await agentService.duplicateAgent(
        currentProject.id,
        duplicateDialog.context.id,
        { name: duplicateName.trim() }
      )

      // Close dialog and navigate to edit the new agent
      setDuplicateDialog(createInitialDialogState())
      setDuplicateName('')
      navigate(`/agents/${response.agent.id}/edit`)
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setDuplicateDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('agents:duplicate.error'),
      }))
    }
  }

  const handleCloseDuplicateDialog = (open: boolean) => {
    if (!open && !duplicateDialog.isProcessing) {
      setDuplicateDialog(createInitialDialogState())
      setDuplicateName('')
    }
  }

  // Delete dialog handlers
  const handleDeleteAgent = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId)
    if (agent) {
      setDeleteDialog({
        isOpen: true,
        isProcessing: false,
        error: null,
        context: { id: agentId, name: agent.name },
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteDialog.context || !currentProject) return

    setDeleteDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await agentService.deleteAgent(currentProject.id, deleteDialog.context.id)

      // Update list removing the deleted agent
      setAgents((prev) => prev.filter((a) => a.id !== deleteDialog.context?.id))

      setDeleteDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setDeleteDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('agents:delete.error'),
      }))
    }
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open && !deleteDialog.isProcessing) {
      setDeleteDialog(createInitialDialogState())
    }
  }

  // History dialog handlers
  const handleHistoryAgent = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId)
    if (agent) {
      setHistoryAgentId(agentId)
      setHistoryAgentName(agent.name)
      setHistoryDialogOpen(true)
    }
  }

  const handleTestAgent = (agentId: string) => {
    navigate(`/agents/${agentId}/edit?playground=true`)
  }

  // No project selected
  if (!currentProject) {
    return <NoProjectState />
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder={t('agents:list.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
          />
        </div>

        {/* Status Filter */}
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {STATUS_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="focus:bg-white/5"
              >
                {t(`agents:list.${option.i18nKey}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select
          value={sortOption}
          onValueChange={(value) => setSortOption(value as SortOption)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {SORT_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="focus:bg-white/5"
              >
                {t(`agents:list.${option.i18nKey}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Create Button */}
        <Button
          variant="action-indigo"
          className="gap-2 whitespace-nowrap"
          onClick={handleCreateAgent}
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">{t('agents:list.newAgent')}</span>
        </Button>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          // Loading skeletons
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <AgentCardSkeleton key={index} />
            ))}
          </>
        ) : sortedAgents.length === 0 ? (
          // Empty state
          <EmptyState hasFilters={hasFilters} />
        ) : (
          // Agent cards
          <>
            {sortedAgents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onClick={() => handleAgentClick(agent.id)}
                onEdit={() => handleEditAgent(agent.id)}
                onDuplicate={() => handleDuplicateAgent(agent.id)}
                onDelete={() => handleDeleteAgent(agent.id)}
                onTest={() => handleTestAgent(agent.id)}
                onHistory={() => handleHistoryAgent(agent.id)}
              />
            ))}
            {/* New agent card at the end */}
            <NewAgentCard onClick={handleCreateAgent} />
          </>
        )}
      </div>

      {/* Duplicate Agent Dialog */}
      <ConfirmationDialog
        open={duplicateDialog.isOpen}
        onOpenChange={handleCloseDuplicateDialog}
        title={t('agents:duplicate.title')}
        description={t('agents:duplicate.description')}
        confirmLabel={t('agents:duplicate.confirm')}
        confirmingLabel={t('agents:duplicate.confirming')}
        confirmVariant="action-indigo"
        isProcessing={duplicateDialog.isProcessing}
        isDisabled={!duplicateName.trim()}
        error={duplicateDialog.error}
        onConfirm={handleConfirmDuplicate}
      >
        <div className="space-y-2">
          <Label htmlFor="duplicate-name" className="text-gray-300">
            {t('agents:duplicate.nameLabel')}
          </Label>
          <Input
            id="duplicate-name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            placeholder={t('agents:duplicate.namePlaceholder')}
            className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            disabled={duplicateDialog.isProcessing}
          />
        </div>
      </ConfirmationDialog>

      {/* Delete Agent Dialog */}
      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={handleCloseDeleteDialog}
        title={t('agents:delete.title')}
        description={
          <>
            {t('agents:delete.message')}{' '}
            <span className="font-semibold text-white">
              {deleteDialog.context?.name}
            </span>
            ?
          </>
        }
        confirmLabel={t('agents:delete.confirm')}
        confirmingLabel={t('agents:delete.confirming')}
        confirmIcon={<Trash2 className="w-4 h-4 mr-2" />}
        confirmVariant="action-red"
        isProcessing={deleteDialog.isProcessing}
        error={deleteDialog.error}
        onConfirm={handleConfirmDelete}
      />

      {/* Agent History Dialog */}
      {currentProject && historyAgentId && (
        <AgentHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          projectId={currentProject.id}
          agentId={historyAgentId}
          agentName={historyAgentName}
        />
      )}
    </div>
  )
}
